import React, { useState } from 'react';
import { TrendingUp, TrendingDown, ChevronRight, ChevronDown, Plus } from 'lucide-react';

const RealEstateAnalytics = () => {
  return (
    <div className="min-h-screen bg-black text-white p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-4xl font-bold mb-1">Marketing Analytics</h1>
        <p className="text-gray-400 text-sm">Real Estate - Property Marketing Performance Overview</p>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        {/* Total Campaigns */}
        <div className="bg-black border-2 border-gray-400 rounded-lg p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
          <p className="text-sm text-gray-400 mb-2">Total Campaigns</p>
          <p className="text-5xl font-bold mb-2">24</p>
          <p className="text-sm text-green-400 mb-3">+4 vs last month</p>
          <div className="flex gap-3 text-xs">
            <div><span className="text-blue-400">◆</span> Open Houses <span className="font-bold">9</span></div>
            <div><span className="text-green-400">◆</span> Social <span className="font-bold">8</span></div>
            <div><span className="text-purple-400">◆</span> Paid Ads <span className="font-bold">7</span></div>
          </div>
        </div>

        {/* Total Leads */}
        <div className="bg-black border-2 border-gray-400 rounded-lg p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
          <p className="text-sm text-gray-400 mb-2">Total Leads</p>
          <p className="text-5xl font-bold mb-2">8.3K</p>
          <p className="text-sm text-green-400 mb-3">+12% vs last month</p>
          <div className="flex gap-3 text-xs">
            <div><span className="text-cyan-400">◆</span> <span className="font-bold">3.2K</span></div>
            <div><span className="text-yellow-400">◆</span> <span className="font-bold">2.8K</span></div>
            <div><span className="text-pink-400">◆</span> <span className="font-bold">2.3K</span></div>
          </div>
        </div>

        {/* Conversion Rate */}
        <div className="bg-black border-2 border-gray-400 rounded-lg p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
          <p className="text-sm text-gray-400 mb-2">Conversion Rate</p>
          <p className="text-5xl font-bold mb-2">4.2%</p>
          <p className="text-sm text-green-400 mb-3">+1.8% vs last month</p>
          <div className="flex gap-3 text-xs">
            <div><span className="text-green-400">◆</span> <span className="font-bold">352</span></div>
            <div><span className="text-blue-400">◆</span> <span className="font-bold">287</span></div>
            <div><span className="text-purple-400">◆</span> <span className="font-bold">245</span></div>
          </div>
        </div>

        {/* Average ROI */}
        <div className="bg-black border-2 border-gray-400 rounded-lg p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
          <p className="text-sm text-gray-400 mb-2">Average ROI</p>
          <p className="text-5xl font-bold mb-2">68%</p>
          <p className="text-sm text-green-400 mb-3">+11.5% vs last month</p>
          <div className="flex gap-3 text-xs">
            <div><span className="text-cyan-400">◆</span> <span className="font-bold">45%</span></div>
            <div><span className="text-yellow-400">◆</span> <span className="font-bold">38%</span></div>
            <div><span className="text-pink-400">◆</span> <span className="font-bold">72%</span></div>
          </div>
        </div>
      </div>

      {/* Middle Section */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        {/* ROI Trend */}
        <div className="lg:col-span-2 bg-black border-2 border-gray-400 rounded-lg p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
          <h2 className="text-xl font-bold mb-4">Return on Investment Trend</h2>
          
          <div className="flex justify-between mb-4">
            <div>
              <p className="text-xs text-gray-400">Investment</p>
              <p className="text-2xl font-bold">$45K</p>
              <p className="text-xs text-red-400">Remaining $8K | -3%</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Return</p>
              <p className="text-2xl font-bold">$182K</p>
              <p className="text-xs text-green-400">+14.2%</p>
            </div>
          </div>

          {/* Bar Chart */}
          <div className="relative h-40">
            <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-500">
              <span>$180K</span>
              <span>$135K</span>
              <span>$90K</span>
              <span>$45K</span>
              <span>$0</span>
            </div>
            <div className="ml-12 h-full flex items-end justify-between gap-2">
              {[
                { inv: 55, ret: 65 },
                { inv: 60, ret: 72 },
                { inv: 52, ret: 68 },
                { inv: 68, ret: 78 },
                { inv: 65, ret: 82 },
                { inv: 72, ret: 88 }
              ].map((data, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                  <div className="relative w-full flex gap-0.5">
                    <div className="flex-1 bg-purple-600 rounded-t" style={{ height: `${data.inv}%` }}></div>
                    <div className="flex-1 bg-cyan-500 rounded-t relative" style={{ height: `${data.ret}%` }}>
                      {i === 5 && <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-500 rounded-full"></div>}
                    </div>
                  </div>
                  <span className="text-[10px] text-gray-500">{['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'][i]}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-4 mt-3 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-600 rounded"></div>
              <span>Investment</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-cyan-500 rounded"></div>
              <span>Return</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Peak Performance</span>
            </div>
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-black border-2 border-gray-400 rounded-lg p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300 flex flex-col justify-center">
          <p className="text-sm text-gray-400 mb-2">Revenue</p>
          <p className="text-4xl font-bold mb-2">$182,000</p>
          <p className="text-2xl font-bold text-green-400">+204% Generated ROI</p>
        </div>

        {/* ROAS */}
        <div className="lg:col-span-2 bg-black border-2 border-gray-400 rounded-lg p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
          <h2 className="text-xl font-bold mb-4">Return On Ad Spend</h2>
          
          <div className="flex justify-between mb-4">
            <div>
              <p className="text-xs text-gray-400">ROAS</p>
              <p className="text-3xl font-bold">5.2x</p>
              <p className="text-xs text-green-400">+2.1 since last month</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Revenue</p>
              <p className="text-xl font-bold">$182K</p>
              <p className="text-xs text-green-400">+28.7%</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Ad Spend</p>
              <p className="text-xl font-bold">$35K</p>
              <p className="text-xs text-gray-400">Budget Used</p>
            </div>
          </div>

          {/* ROAS Trend */}
          <div className="flex items-end justify-between h-24 gap-1 mb-3">
            {[45, 52, 48, 65, 58, 72, 68, 78, 75, 85, 82, 88].map((height, i) => (
              <div key={i} className="flex-1 bg-teal-500 rounded-t" style={{ height: `${height}%` }}></div>
            ))}
          </div>

          <div className="flex justify-between text-xs mb-2">
            <span className="text-gray-400">Low: 2.1X</span>
            <span className="text-gray-400">Average: 4.2X</span>
            <span className="text-gray-400">High: 5.8X</span>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-green-400">Excellent Performance - Above Industry Average (3.5X)</span>
          </div>
        </div>
      </div>

      {/* Third Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Geographic Reach */}
        <div className="bg-black border-2 border-gray-400 rounded-lg p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
          <h2 className="text-xl font-bold mb-4">Geographic Reach Insights</h2>
          
          <div className="flex gap-6 mb-4">
            <div>
              <p className="text-xs text-gray-400">Markets Reached</p>
              <p className="text-3xl font-bold">12</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Leads Generated</p>
              <p className="text-3xl font-bold">8.3K</p>
            </div>
          </div>

          {/* Simplified Map Representation */}
          <div className="relative h-32 bg-gray-900 rounded-lg mb-4 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-blue-400 rounded-full animate-pulse"></div>
            <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-pink-400 rounded-full animate-pulse"></div>
            <div className="absolute bottom-1/4 left-1/2 w-5 h-5 bg-yellow-400 rounded-full animate-pulse"></div>
            <div className="absolute top-1/2 right-1/4 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <div className="absolute bottom-1/3 left-1/3 w-4 h-4 bg-orange-400 rounded-full animate-pulse"></div>
          </div>

          {/* Location Stats */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="flex items-center gap-2"><span className="text-blue-400">●</span> New York</span> <span className="font-bold">2,345</span></div>
            <div className="flex justify-between"><span className="flex items-center gap-2"><span className="text-pink-400">●</span> Los Angeles</span> <span className="font-bold">1,892</span></div>
            <div className="flex justify-between"><span className="flex items-center gap-2"><span className="text-yellow-400">●</span> Miami</span> <span className="font-bold">1,234</span></div>
            <div className="flex justify-between"><span className="flex items-center gap-2"><span className="text-green-400">●</span> San Francisco</span> <span className="font-bold">987</span></div>
            <div className="flex justify-between"><span className="flex items-center gap-2"><span className="text-orange-400">●</span> Chicago</span> <span className="font-bold">845</span></div>
            <div className="flex justify-between"><span className="flex items-center gap-2"><span className="text-purple-400">●</span> Others</span> <span className="font-bold">997</span></div>
          </div>
        </div>

        {/* Actionable Insights */}
        <div className="bg-black border-2 border-gray-400 rounded-lg p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
          <h2 className="text-xl font-bold mb-4">Actionable Insights</h2>
          
          <div className="space-y-3">
            <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="px-2 py-1 bg-red-600 text-xs rounded font-semibold">HIGH PRIORITY</span>
              </div>
              <p className="text-sm mb-3">Facebook: Ad spend reached 92% of budget - campaigns will pause soon.</p>
              <button className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded text-sm font-semibold transition-all">
                Add Budget +
              </button>
            </div>

            <div className="bg-green-900 bg-opacity-30 border border-green-500 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="px-2 py-1 bg-green-600 text-xs rounded font-semibold">OPPORTUNITY</span>
              </div>
              <p className="text-sm mb-3">Instagram: Conversion rate is 42% above target - expand to new luxury markets.</p>
              <button className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded text-sm font-semibold transition-all">
                View Campaign
              </button>
            </div>

            <div className="bg-yellow-900 bg-opacity-30 border border-yellow-500 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="px-2 py-1 bg-yellow-600 text-xs rounded font-semibold">REMINDER</span>
              </div>
              <p className="text-sm">Q4 Open House season starts next month - schedule marketing team sync.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Performing Campaigns */}
      <div className="bg-black border-2 border-gray-400 rounded-lg p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
        <h2 className="text-xl font-bold mb-4">Top Performing Campaigns</h2>
        
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[
            { platform: 'Instagram', name: 'Luxury Condo Showcase', leads: '2.8K', conv: '8.2%', roi: '245%', budget: '$4.5K / $6K', status: 'Active', color: 'pink' },
            { platform: 'Facebook', name: 'Downtown Lofts Campaign', leads: '2.3K', conv: '6.5%', roi: '198%', budget: '$3.8K / $5K', status: 'Active', color: 'blue' },
            { platform: 'Google Ads', name: 'Premium Properties Q3', leads: '1.9K', conv: '7.8%', roi: '215%', budget: '$5.2K / $7K', status: 'Active', color: 'red' },
            { platform: 'LinkedIn', name: 'Commercial Real Estate', leads: '1.2K', conv: '5.4%', roi: '178%', budget: '$2.9K / $4K', status: 'Active', color: 'blue' },
            { platform: 'YouTube', name: 'Virtual Tour Series', leads: '1.6K', conv: '6.9%', roi: '189%', budget: '$4.1K / $5.5K', status: 'Active', color: 'red' },
            { platform: 'Pinterest', name: 'Dream Home Gallery', leads: '980', conv: '4.8%', roi: '156%', budget: '$2.2K / $3K', status: 'Paused', color: 'red' }
          ].map((campaign, i) => (
            <div key={i} className="flex-shrink-0 w-72 bg-gray-900 border border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full bg-${campaign.color}-600 flex items-center justify-center text-xs font-bold`}>
                    {campaign.platform[0]}
                  </div>
                  <span className="font-semibold text-sm">{campaign.platform}</span>
                </div>
                <span className={`px-2 py-1 text-xs rounded ${campaign.status === 'Active' ? 'bg-green-600' : 'bg-yellow-600'}`}>
                  {campaign.status}
                </span>
              </div>
              
              <h3 className="font-semibold mb-3">{campaign.name}</h3>
              
              <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                <div>
                  <p className="text-gray-400">Leads</p>
                  <p className="font-bold">{campaign.leads}</p>
                </div>
                <div>
                  <p className="text-gray-400">Conv.</p>
                  <p className="font-bold">{campaign.conv}</p>
                </div>
                <div>
                  <p className="text-gray-400">ROI</p>
                  <p className="font-bold">{campaign.roi}</p>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-400">Budget</span>
                  <span className="font-semibold">{campaign.budget}</span>
                </div>
                <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden">
                  <div className="bg-blue-500 h-full" style={{ width: `${(parseFloat(campaign.budget.split('/')[0].replace('$', '').replace('K', '')) / parseFloat(campaign.budget.split('/')[1].replace('$', '').replace('K', ''))) * 100}%` }}></div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button className="flex-1 py-2 bg-gray-800 hover:bg-gray-700 rounded text-xs font-semibold transition-all">
                  {campaign.status === 'Active' ? 'Pause' : 'Resume'}
                </button>
                <button className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-xs font-bold transition-all">
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RealEstateAnalytics;
